import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, CheckCircle2, AlertTriangle, Download, KeyRound, Trash2, ScanLine, ShieldCheck, FileSpreadsheet, Files, ArrowLeft, Link2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { parseStatementText, parseCsvRows } from "../lib/parser";
import { categorize } from "../lib/categorize";
import { sampleStatementPDF, download } from "../lib/reports";
import { fmt, dateLabel, round2, uid, monthLabel } from "../lib/format";
import { PageHeader, Section, NativeSelect, CategorySelect, Banner, Money } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { cn } from "../lib/utils";

export const uploadHandoff = { current: null };

const STEPS = ["Upload", "Extract", "Categorize", "Review", "Budget", "Dashboard", "Save"];
const typeLabel = { checking: "Checking", savings: "Savings", credit: "Credit Card", cash: "Cash" };

function Stepper({ step }) {
  return (
    <ol className="flex flex-wrap gap-2 mb-6" data-testid="setup-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className={cn("flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium", i < step ? "bg-primary text-primary-foreground border-primary" : i === step ? "border-primary text-primary" : "text-muted-foreground")}>
          <span className="num">{i + 1}</span> {s}
        </li>
      ))}
    </ol>
  );
}

const flagOcr = (p) => p.transactions.forEach((t) => { t.flagged = true; t.flagReason = [t.flagReason, "Read by OCR — please verify"].filter(Boolean).join("; "); });

async function parseFile(file, { password, forceOcr, onProgress }) {
  if (/\.csv$/i.test(file.name) || file.type === "text/csv") {
    const res = await new Promise((resolve, reject) => Papa.parse(file, { header: true, skipEmptyLines: true, complete: resolve, error: reject }));
    const parsed = parseCsvRows(res.data);
    if (!parsed.transactions.length) throw new Error(parsed.warnings[0] || "No transactions found");
    return { ...parsed, bank: null, accountType: "checking", last4: null, period: null, openingBalance: null, closingBalance: null, reconciliation: null, source: "csv" };
  }
  if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") throw new Error("Not a PDF or CSV file");
  const { extractPdf } = await import("../lib/pdfExtract");
  const out = await extractPdf(await file.arrayBuffer(), { password, forceOcr, onProgress });
  let parsed = parseStatementText(out.text);
  let ocr = out.ocr;
  if (!parsed.transactions.length && !out.ocr) {
    onProgress?.({ stage: "No text rows found — trying OCR", page: 1, total: out.pages });
    const out2 = await extractPdf(await file.arrayBuffer(), { password, forceOcr: true, onProgress });
    parsed = parseStatementText(out2.text);
    ocr = true;
  }
  if (ocr) flagOcr(parsed);
  return { ...parsed, ocr, pages: out.pages, source: "pdf" };
}

function ReviewPanel({ item, setItem, onImport, onBack, batch }) {
  const { state } = useStore();
  const [onlyReview, setOnlyReview] = useState(false);
  const { parsed, rows, acct } = item;
  const setRow = (key, p) => setItem({ rows: rows.map((r) => (r.key === key ? { ...r, ...p } : r)) });
  const included = rows.filter((r) => r.include);
  const needsReview = included.filter((r) => r.flagged).length;
  return (
    <div className="space-y-5" data-testid="upload-review">
      {batch && <Button variant="ghost" onClick={onBack} data-testid="back-to-batch-button"><ArrowLeft className="h-4 w-4 mr-1" /> Back to all statements</Button>}
      <div className="grid md:grid-cols-4 gap-3">
        <Section className="md:col-span-2" testid="statement-details">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Statement</p>
          <p className="font-display text-xl font-bold mt-1" data-testid="detected-bank">{parsed.bank || "Bank not recognized"}</p>
          <p className="text-sm text-muted-foreground" data-testid="detected-period">{parsed.period ? `${parsed.period.start ? dateLabel(parsed.period.start) : "?"} – ${dateLabel(parsed.period.end)}` : "Period not found"} · {item.name}{parsed.ocr ? " · OCR" : ""}</p>
          <div className="flex gap-6 mt-3 text-sm">
            <div><p className="text-muted-foreground text-xs">Opening</p><p className="num" data-testid="opening-balance">{parsed.openingBalance !== null ? fmt(parsed.openingBalance) : "—"}</p></div>
            <div><p className="text-muted-foreground text-xs">Closing</p><p className="num" data-testid="closing-balance">{parsed.closingBalance !== null ? fmt(parsed.closingBalance) : "—"}</p></div>
            <div><p className="text-muted-foreground text-xs">Found</p><p className="num" data-testid="found-count">{rows.length}</p></div>
          </div>
        </Section>
        <Section className="md:col-span-2" testid="reconciliation-card">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Reconciliation</p>
          <Reconciliation parsed={parsed} />
          {parsed.warnings?.map((w) => <p key={w} className="text-xs text-warning mt-1">{w}</p>)}
        </Section>
      </div>

      <Section title="Which account is this?">
        <AccountPicker acct={acct} member={item.member} onChange={(p) => setItem(p)} accounts={state.accounts} members={state.members} />
      </Section>

      {needsReview > 0 && <Banner tone="warning" icon={AlertTriangle} testid="review-needed-banner"><b>{needsReview} item(s) flagged for your confirmation.</b> We never guess missing details — please check the highlighted rows.</Banner>}

      <Section title={`Transactions (${included.length} selected)`} action={<div className="flex items-center gap-2"><Switch checked={onlyReview} onCheckedChange={setOnlyReview} data-testid="only-review-switch" /><span className="text-sm">Only flagged</span></div>}>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm min-w-[760px]" data-testid="review-table">
            <thead className="text-xs uppercase text-muted-foreground border-b">
              <tr><th className="p-2 w-8"></th><th className="p-2 text-left">Date</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Category</th><th className="p-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {rows.filter((r) => !onlyReview || r.flagged).map((r) => (
                <tr key={r.key} className={cn("border-b last:border-0", r.flagged && r.include && "bg-warning-soft", !r.include && "opacity-50")} data-testid={`review-row-${r.key}`}>
                  <td className="p-2"><Checkbox checked={r.include} onCheckedChange={(v) => setRow(r.key, { include: !!v })} data-testid={`review-include-${r.key}`} /></td>
                  <td className="p-2"><Input type="date" value={r.date} onChange={(e) => setRow(r.key, { date: e.target.value })} className="h-9 w-[150px]" /></td>
                  <td className="p-2"><Input value={r.description} onChange={(e) => setRow(r.key, { description: e.target.value })} className="h-9 min-w-[220px]" /></td>
                  <td className="p-2"><Input type="number" step="0.01" value={r.amount} onChange={(e) => setRow(r.key, { amount: parseFloat(e.target.value) || 0 })} className={cn("h-9 w-[120px] text-right num", r.amount > 0 ? "text-income" : "text-expense")} data-testid={`review-amount-${r.key}`} /></td>
                  <td className="p-2"><CategorySelect categories={state.categories} value={r.category} onChange={(v) => setRow(r.key, { category: v, flagged: false })} className="h-9 w-[200px]" testid={`review-category-${r.key}`} /></td>
                  <td className="p-2 space-x-1">
                    {r.duplicate && <Badge variant="secondary">Duplicate</Badge>}
                    {r.flagged && <Badge className="bg-warning-soft text-warning border-0" title={r.flagReason}>Check</Badge>}
                    {r.flagged && <button className="text-xs underline text-muted-foreground" onClick={() => setRow(r.key, { flagged: false })} data-testid={`review-confirm-${r.key}`}>OK</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Amounts: <span className="text-income">positive = money in</span>, <span className="text-expense">negative = money out</span>. Hover "Check" to see why an item was flagged.</p>
      </Section>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <p className="text-sm">In: <Money value={included.filter((r) => r.amount > 0).reduce((s, r) => s + r.amount, 0)} className="text-income" /> · Out: <Money value={-included.filter((r) => r.amount < 0).reduce((s, r) => s + r.amount, 0)} className="text-expense" /></p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} data-testid="cancel-import-button">{batch ? "Done reviewing" : "Cancel"}</Button>
          <Button onClick={onImport} className="rounded-full px-6" disabled={!included.length} data-testid="confirm-import-button">Import {included.length} transactions</Button>
        </div>
      </div>
    </div>
  );
}

function Reconciliation({ parsed, compact }) {
  const r = parsed.reconciliation;
  if (!r) return <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm mt-2")} data-testid="reconciliation-status">{compact ? "Totals not found" : "Statement totals not found, so we can't double-check automatically."}</p>;
  if (r.ok) return <p className={cn("flex items-center gap-1.5 text-income font-semibold", compact ? "text-xs" : "mt-2")} data-testid="reconciliation-status"><CheckCircle2 className="h-4 w-4" /> {compact ? "Reconciled" : "Totals match the statement"}</p>;
  return <p className={cn("flex items-center gap-1.5 text-warning font-semibold", compact ? "text-xs" : "mt-2")} data-testid="reconciliation-status"><AlertTriangle className="h-4 w-4" /> Off by {fmt(Math.abs(r.difference ?? 0))}{compact ? "" : " — review flagged rows"}</p>;
}

function AccountPicker({ acct, member, onChange, accounts, members, compact }) {
  return (
    <div className={cn("grid gap-2", compact ? "grid-cols-1 sm:grid-cols-2" : "sm:grid-cols-4 gap-3")}>
      <NativeSelect value={acct.id} onChange={(v) => { const a = accounts.find((x) => x.id === v); onChange({ acct: a ? { id: a.id, name: a.name, type: a.type } : { ...acct, id: "new" } }); }} className={compact ? "h-9" : ""} testid="import-account-select">
        <option value="new" label={`+ New: ${acct.id === "new" ? acct.name : "account"}`} />
        {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </NativeSelect>
      {!compact && <Input value={acct.name} disabled={acct.id !== "new"} onChange={(e) => onChange({ acct: { ...acct, name: e.target.value } })} placeholder="Account name" data-testid="import-account-name-input" />}
      <NativeSelect value={acct.type} disabled={acct.id !== "new"} onChange={(v) => onChange({ acct: { ...acct, type: v } })} className={compact ? "h-9" : ""} testid="import-account-type-select">
        {Object.entries(typeLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </NativeSelect>
      {!compact && (
        <NativeSelect value={member} onChange={(v) => onChange({ member: v })} testid="import-member-select">
          {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </NativeSelect>
      )}
    </div>
  );
}

export default function UploadPage() {
  const { state, importTransactions, isDuplicate, update } = useStore();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const csvRef = useRef(null);
  const itemsRef = useRef([]);
  const busy = useRef(false);
  const [items, setItemsState] = useState([]);
  const [reviewId, setReviewId] = useState(null);
  const [drag, setDrag] = useState(false);
  const [forceOcr, setForceOcr] = useState(false);
  const [done, setDone] = useState(null);
  const [pw, setPw] = useState({});

  const setItems = (list) => { itemsRef.current = list; setItemsState(list); };
  const patch = (id, p) => setItems(itemsRef.current.map((it) => (it.id === id ? { ...it, ...p } : it)));

  const prepare = (parsed, preset) => {
    const known = state.accounts.find((a) => parsed.last4 && a.last4 === parsed.last4) || state.accounts.find((a) => a.id === preset?.accountId);
    const type = known?.type || parsed.accountType || "checking";
    const acct = known ? { id: known.id, name: known.name, type: known.type } : { id: "new", key: `new-${parsed.bank || "x"}-${parsed.last4 || "x"}-${type}`, name: `${parsed.bank || "My"} ${typeLabel[type]}${parsed.last4 ? ` ••${parsed.last4}` : ""}`, type, institution: parsed.bank, last4: parsed.last4 };
    const dupCheck = known ? isDuplicate(known.id) : () => false;
    const rows = parsed.transactions.map((t, i) => {
      const c = categorize(t, state.rules, state.categories);
      const category = type === "credit" && c.category === "other-income" ? "unclassified-transactions" : c.category;
      const dup = dupCheck(t);
      return { key: i, ...t, category, include: !dup && !(t.amount === 0 && t.flagged), duplicate: dup, flagged: t.flagged || !c.confident, flagReason: [t.flagReason, !c.confident ? "Category uncertain" : ""].filter(Boolean).join("; ") };
    });
    return { rows, acct, member: "m1" };
  };

  const processQueue = async () => {
    if (busy.current) return;
    busy.current = true;
    const { PasswordRequiredError } = await import("../lib/pdfExtract");
    let next;
    while ((next = itemsRef.current.find((i) => i.status === "queued"))) {
      const it = next;
      patch(it.id, { status: "processing", progress: "Opening…" });
      try {
        const parsed = await parseFile(it.file, { password: it.password, forceOcr, onProgress: (p) => patch(it.id, { progress: `${p.stage} — page ${p.page} of ${p.total}` }) });
        patch(it.id, { status: "ready", parsed, password: undefined, ...prepare(parsed, it.preset) });
      } catch (e) {
        if (e instanceof PasswordRequiredError) patch(it.id, { status: "password", pwError: e.incorrect ? "Incorrect password" : "", password: undefined });
        else { console.error(e); patch(it.id, { status: "error", error: e.message || "Could not read this file" }); }
      }
    }
    busy.current = false;
    const list = itemsRef.current;
    if (list.length === 1 && list[0].status === "ready") setReviewId(list[0].id);
  };

  const addFiles = (fileList, preset) => {
    const files = [...fileList].filter((f) => /\.(pdf|csv)$/i.test(f.name) || ["application/pdf", "text/csv"].includes(f.type));
    if (!files.length) return toast.error("Please choose PDF or CSV files.");
    setDone(null);
    setItems([...itemsRef.current, ...files.map((f) => ({ id: uid(), file: f, name: f.name, status: "queued", preset }))]);
    processQueue();
  };

  const newIds = useRef({});
  const importItem = (it) => {
    if (!it.acct.name?.trim()) { toast.error(`Name the account for ${it.name}`); return null; }
    const bad = it.rows.find((r) => r.include && (!r.date || !Number(r.amount)));
    if (bad) { toast.error(`${it.name}: fix the date/amount for "${bad.description}" or untick it`); return null; }
    const accountId = it.acct.id !== "new" ? it.acct.id : (newIds.current[it.acct.key || it.id] ||= `acc-${uid()}`);
    const dup = isDuplicate(accountId);
    const keep = it.rows.filter((r) => r.include && (r.duplicate || !dup(r)));
    const skipped = it.rows.filter((r) => r.include).length - keep.length + it.rows.filter((r) => r.duplicate && !r.include).length;
    const res = importTransactions(
      keep.map((r) => ({ date: r.date, description: r.description, amount: round2(r.amount), category: r.category, flagged: !!r.flagged, flagReason: r.flagged ? r.flagReason : "", memberId: it.member, autoCategory: true })),
      { account: { ...it.acct, id: accountId }, statement: { ...it.parsed, fileName: it.name, skipped } }
    );
    patch(it.id, { status: "imported", result: { count: keep.length, skipped, matched: res.matched } });
    if (it.preset?.month) {
      const hit = keep.some((r) => r.date.startsWith(it.preset.month));
      setTimeout(() => (hit ? toast.success(`${monthLabel(it.preset.month)} is now covered for ${it.acct.name}`) : toast.warning(`This file had no ${monthLabel(it.preset.month)} transactions — the gap is still open`)), 400);
    }
    return { count: keep.length, skipped, matched: res.matched, year: (it.parsed.period?.end || keep[0]?.date || "").slice(0, 4) };
  };

  const importOne = (it) => {
    const r = importItem(it);
    if (!r) return;
    setReviewId(null);
    toast.dismiss();
    toast.success(`Imported ${r.count} transactions${r.matched ? ` · linked ${r.matched} transfer(s)` : ""}`);
    if (itemsRef.current.length === 1) setDone({ ...r, files: 1 });
  };

  const importAll = () => {
    const ready = itemsRef.current.filter((i) => i.status === "ready").sort((a, b) => (a.parsed.period?.end || "").localeCompare(b.parsed.period?.end || ""));
    const tot = { count: 0, skipped: 0, matched: 0, files: 0 };
    ready.forEach((it) => {
      const r = importItem(itemsRef.current.find((x) => x.id === it.id));
      if (r) { tot.count += r.count; tot.skipped += r.skipped; tot.matched += r.matched; tot.files += 1; tot.year = r.year || tot.year; }
    });
    if (tot.files) { setDone(tot); toast.dismiss(); toast.success(`Imported ${tot.count} transactions from ${tot.files} statement(s)`); }
  };

  const deleteStatement = (st) =>
    update("Delete statement", (d) => {
      d.transactions = d.transactions.filter((t) => t.statementId !== st.id);
      d.statements = d.statements.filter((s) => s.id !== st.id);
    }, [{ action: "delete", summary: `Removed statement ${st.fileName} and its transactions` }]);

  useEffect(() => {
    const h = uploadHandoff.current;
    if (h) {
      uploadHandoff.current = null;
      addFiles(h.files, { accountId: h.accountId, month: h.month });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const presetItem = items.find((i) => i.preset?.month && i.status !== "imported");

  const reviewItem = items.find((i) => i.id === reviewId && i.status === "ready");
  const readyCount = items.filter((i) => i.status === "ready").length;
  const working = items.some((i) => ["queued", "processing"].includes(i.status));
  const step = done ? 4 : reviewItem ? 3 : working ? 1 : items.length ? 3 : 0;
  const ready = items.filter((i) => i.status === "ready");
  const newAccounts = [...new Set(ready.filter((i) => i.acct.id === "new").map((i) => i.acct.key))].length;

  return (
    <div data-testid="upload-page">
      <PageHeader title="Upload statements" subtitle="Add one statement or a whole year at once — PDF or CSV. Everything is read on this device; nothing is uploaded anywhere.">
        <Button variant="outline" onClick={sampleStatementPDF} data-testid="download-sample-statement-button"><Download className="h-4 w-4 mr-2" /> Sample statement PDF</Button>
      </PageHeader>
      <Stepper step={step} />
      {presetItem && <div className="mb-5"><Banner icon={Files} testid="missing-month-banner">Importing the missing <b>{monthLabel(presetItem.preset.month)}</b> statement for <b>{state.accounts.find((a) => a.id === presetItem.preset.accountId)?.name || "your account"}</b>.</Banner></div>}

      {reviewItem ? (
        <ReviewPanel item={reviewItem} batch={items.length > 1} setItem={(p) => patch(reviewItem.id, p)} onImport={() => importOne(reviewItem)} onBack={() => { setReviewId(null); if (items.length === 1) setItems([]); }} />
      ) : done ? (
        <Section className="text-center py-14" testid="import-done">
          <CheckCircle2 className="h-12 w-12 mx-auto text-income" />
          <p className="font-display text-2xl font-bold mt-4">{done.files > 1 ? `${done.files} statements imported!` : "Your statement is in!"}</p>
          <p className="text-muted-foreground mt-1 max-w-lg mx-auto" data-testid="import-done-summary">{done.count} transactions added{done.skipped ? `, ${done.skipped} duplicates skipped` : ""}{done.matched ? `, ${done.matched} transfers between your accounts linked so they aren't double-counted` : ""}. Your budget has been updated — review it and add anything the statements didn't include.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Button className="rounded-full" onClick={() => nav(`/reports?tab=import${done.year ? `&year=${done.year}` : ""}`)} data-testid="view-import-report-button">View import report</Button>
            <Button variant="outline" className="rounded-full" onClick={() => nav("/budget")} data-testid="go-to-budget-button">Review my budget</Button>
            <Button variant="outline" className="rounded-full" onClick={() => nav("/")} data-testid="go-to-dashboard-button">Open dashboard</Button>
            <Button variant="ghost" onClick={() => { setDone(null); setItems([]); }} data-testid="import-another-button">Import more statements</Button>
          </div>
        </Section>
      ) : (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <label
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
                className={cn("flex flex-col items-center justify-center text-center rounded-3xl border-2 border-dashed cursor-pointer transition-colors duration-200 bg-card", items.length ? "p-6" : "p-10 sm:p-16", drag ? "border-primary bg-accent" : "hover:border-primary/60")}
                data-testid="upload-statement-dropzone"
              >
                <div className="h-14 w-14 rounded-2xl bg-accent grid place-items-center"><UploadCloud className="h-7 w-7 text-primary" /></div>
                <p className="font-display text-xl sm:text-2xl font-bold mt-4">{items.length ? "Add more statements" : "Drop your statements here"}</p>
                <p className="text-muted-foreground mt-1">PDF statements or CSV exports — one file or a whole year</p>
                <Button className="mt-5 rounded-full h-11 px-6" type="button" onClick={(e) => { e.preventDefault(); fileRef.current?.click(); }} data-testid="choose-file-button">Choose files</Button>
                <input ref={fileRef} type="file" multiple accept="application/pdf,.pdf,.csv,text/csv" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
              </label>
              <div className="flex items-center gap-3 mt-4">
                <Switch id="ocr" checked={forceOcr} onCheckedChange={setForceOcr} data-testid="force-ocr-switch" />
                <Label htmlFor="ocr" className="text-sm text-muted-foreground">My statements are scans/photos (use on-device OCR). We also switch to OCR automatically if no text is found.</Label>
              </div>
            </div>
            <div className="lg:col-span-4 space-y-6">
              <Section title="Bank PDFs not great? Use CSV" subtitle="Most banks let you download transactions as CSV (often called “Export” or “Download activity”). It's usually more accurate than a PDF." testid="csv-import-card" action={<FileSpreadsheet className="h-5 w-5 text-primary" />}>
                <Button variant="outline" className="w-full rounded-full" onClick={() => csvRef.current?.click()} data-testid="csv-choose-button"><FileSpreadsheet className="h-4 w-4 mr-2" /> Choose CSV files</Button>
                <input ref={csvRef} type="file" multiple accept=".csv,text/csv" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="csv-file-input" />
                <ul className="text-xs text-muted-foreground space-y-1 mt-3">
                  <li>• Columns are detected automatically: <b>Date</b>, <b>Description</b>, and either <b>Amount</b> or <b>Debit</b>/<b>Credit</b>.</li>
                  <li>• You'll review every row before anything is saved.</li>
                </ul>
                <Button variant="link" className="px-0 h-auto mt-2 text-xs" onClick={() => download("Date,Description,Amount\n2026-05-01,Paycheck ACME Corp,2500.00\n2026-05-03,Grocery Store,-84.12\n", "budget-import-template.csv", "text/csv")} data-testid="csv-template-button"><Download className="h-3.5 w-3.5 mr-1" /> Download a CSV template</Button>
              </Section>
              <Section title="What works">
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li className="flex gap-2"><Files className="h-4 w-4 mt-0.5 text-primary" /> Batch import: drop 12 monthly statements at once</li>
                  <li className="flex gap-2"><FileText className="h-4 w-4 mt-0.5 text-primary" /> Checking, savings and credit card statements</li>
                  <li className="flex gap-2"><ScanLine className="h-4 w-4 mt-0.5 text-primary" /> Scanned statements via on-device OCR</li>
                  <li className="flex gap-2"><KeyRound className="h-4 w-4 mt-0.5 text-primary" /> Password-protected PDFs (never stored)</li>
                  <li className="flex gap-2"><FileSpreadsheet className="h-4 w-4 mt-0.5 text-primary" /> CSV with Date / Description / Amount</li>
                  <li className="flex gap-2"><Link2 className="h-4 w-4 mt-0.5 text-primary" /> Transfers between your accounts are linked automatically</li>
                  <li className="flex gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 text-income" /> Duplicates and overlapping statements are skipped</li>
                </ul>
              </Section>
            </div>
          </div>

          {items.length > 0 && (
            <Section title={`Statements in this batch (${items.length})`} subtitle={working ? "Reading files one at a time…" : `${readyCount} ready to import${newAccounts ? ` · ${newAccounts} new account(s) will be created` : ""}`} testid="batch-list"
              action={<div className="flex gap-2"><Button variant="ghost" size="sm" onClick={() => setItems(items.filter((i) => ["queued", "processing"].includes(i.status)))} disabled={working} data-testid="clear-batch-button">Clear</Button><Button className="rounded-full" onClick={importAll} disabled={!readyCount || working} data-testid="import-all-button">Import all ({readyCount})</Button></div>}>
              <ul className="divide-y">
                {items.map((it) => (
                  <li key={it.id} className="py-3 grid lg:grid-cols-12 gap-3 items-center" data-testid={`batch-item-${it.id}`}>
                    <div className="lg:col-span-4 min-w-0">
                      <p className="font-medium truncate">{it.name}</p>
                      {it.status === "ready" && <p className="text-xs text-muted-foreground">{it.parsed.bank || "Unknown bank"} · {it.parsed.period ? `${dateLabel(it.parsed.period.start || it.parsed.period.end)} – ${dateLabel(it.parsed.period.end)}` : "period not found"} · {it.rows.length} rows{it.rows.some((r) => r.duplicate) ? ` · ${it.rows.filter((r) => r.duplicate).length} duplicates` : ""}</p>}
                      {it.status === "processing" && <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="upload-progress-text"><Loader2 className="h-3 w-3 animate-spin" /> {it.progress}</p>}
                      {it.status === "queued" && <p className="text-xs text-muted-foreground">Waiting…</p>}
                      {it.status === "error" && <p className="text-xs text-expense">{it.error}</p>}
                      {it.status === "imported" && <p className="text-xs text-income" data-testid={`batch-item-result-${it.id}`}>Imported {it.result.count}{it.result.skipped ? ` · ${it.result.skipped} duplicates skipped` : ""}{it.result.matched ? ` · ${it.result.matched} transfers linked` : ""}</p>}
                    </div>
                    <div className="lg:col-span-5">
                      {it.status === "ready" && <AccountPicker compact acct={it.acct} member={it.member} accounts={state.accounts} members={state.members} onChange={(p) => patch(it.id, p.acct && p.acct.id === "new" && !p.acct.key ? { acct: { ...p.acct, key: `new-${it.id}` } } : p)} />}
                      {it.status === "password" && (
                        <div className="flex gap-2 items-center" data-testid={`batch-password-${it.id}`}>
                          <KeyRound className="h-4 w-4 text-warning shrink-0" />
                          <Input type="password" placeholder="PDF password" value={pw[it.id] || ""} onChange={(e) => setPw({ ...pw, [it.id]: e.target.value })} className="h-9" data-testid="pdf-password-input" />
                          <Button size="sm" disabled={!pw[it.id]} onClick={() => { patch(it.id, { status: "queued", password: pw[it.id] }); setPw({ ...pw, [it.id]: "" }); processQueue(); }} data-testid="pdf-password-submit">Open</Button>
                          {it.pwError && <span className="text-xs text-expense whitespace-nowrap" data-testid="pdf-password-error">{it.pwError}</span>}
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-3 flex items-center gap-2 lg:justify-end">
                      {it.status === "ready" && <><Reconciliation compact parsed={it.parsed} />{it.rows.some((r) => r.flagged && r.include) && <Badge className="bg-warning-soft text-warning border-0">{it.rows.filter((r) => r.flagged && r.include).length} to check</Badge>}{it.rows.some((r) => !r.include && !r.duplicate && !Number(r.amount)) && <Badge className="bg-expense-soft text-expense border-0" title="These rows were left out because no amount was found. Open Review to add the amount." data-testid={`missing-amount-${it.id}`}>{it.rows.filter((r) => !r.include && !r.duplicate && !Number(r.amount)).length} need an amount</Badge>}<Button size="sm" variant="outline" onClick={() => setReviewId(it.id)} data-testid={`review-item-${it.id}`}>Review</Button></>}
                      {!["processing", "imported"].includes(it.status) && <Button variant="ghost" size="icon" onClick={() => setItems(itemsRef.current.filter((x) => x.id !== it.id))} data-testid={`remove-item-${it.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      {state.statements.length > 0 && !reviewItem && (
        <Section title="Imported statements" className="mt-6" testid="statement-history">
          <ul className="divide-y">
            {state.statements.slice().reverse().map((s) => (
              <li key={s.id} className="flex items-center justify-between py-3 gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{s.fileName || "Statement"} <span className="text-muted-foreground font-normal">· {s.bank || state.accounts.find((a) => a.id === s.accountId)?.name}</span></p>
                  <p className="text-xs text-muted-foreground">{s.count} transactions · imported {new Date(s.importedAt).toLocaleDateString()} {s.reconciliation ? (s.reconciliation.ok ? "· reconciled ✓" : "· totals didn't match") : ""}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => deleteStatement(s)} data-testid={`delete-statement-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
